import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../hooks/redux';
import { useAuth } from '../../hooks/useAuth';
import { setUser } from '../../store/slices/authSlice';
import { useUpdateProfileMutation } from '../../store/api/localApi';

/**
 * Last step of invite signup: ask for a contact number and address so a new
 * user has them on file from day one rather than having to find the settings
 * page later. Both fields are optional and this step is never mandatory —
 * getting stuck here means never finishing signup.
 */
const SignupContactDetails: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAuth();
  const [updateProfile, { isLoading: saving }] = useUpdateProfileMutation();

  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [error, setError] = useState('');

  const goToDashboard = () => navigate('/dashboard');

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const updates: { phone?: string; address?: string } = {};
    if (phone.trim()) updates.phone = phone.trim();
    if (address.trim()) updates.address = address.trim();

    // PUT /profile rejects an empty body, so an all-empty Confirm behaves
    // exactly like Skip instead of stranding the user on a 400.
    if (Object.keys(updates).length === 0) {
      goToDashboard();
      return;
    }

    try {
      await updateProfile(updates).unwrap();
      if (user) {
        dispatch(setUser({ ...user, ...updates }));
      }
      goToDashboard();
    } catch (err: any) {
      const data = err?.data || {};
      setError(data.error || data.message || 'Failed to save your contact details');
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8"
      data-testid="signup-contact-details-page"
    >
      <div className="max-w-md w-full space-y-8">
        <div className="bg-white shadow rounded-lg p-8">
          <p className="text-center text-xs font-medium uppercase tracking-wide text-gray-400 mb-2">
            Step 2 of 2
          </p>
          <h2 className="text-center text-2xl font-bold text-gray-900">How can we reach you?</h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Add a contact number and address so we can reach you quickly. You can always add these
            later in your settings.
          </p>

          <form onSubmit={handleConfirm} className="mt-6 space-y-4">
            {error && (
              <div
                data-testid="signup-contact-error"
                className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm"
              >
                {error}
              </div>
            )}

            <div>
              <label
                htmlFor="signup-contact-phone"
                className="block text-sm font-medium text-gray-700"
              >
                Phone number <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                id="signup-contact-phone"
                type="tel"
                name="tel"
                autoComplete="tel"
                data-testid="signup-contact-phone"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                placeholder="+353 87 123 4567"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>

            <div>
              <label
                htmlFor="signup-contact-address"
                className="block text-sm font-medium text-gray-700"
              >
                Address <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                id="signup-contact-address"
                type="text"
                name="street-address"
                autoComplete="street-address"
                data-testid="signup-contact-address"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                placeholder="12 Oak Road, Dublin 4"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
            </div>

            <button
              type="submit"
              disabled={saving}
              data-testid="signup-contact-confirm"
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Confirm'}
            </button>

            {/* Never disabled: a failed save must not leave the user stuck here. */}
            <button
              type="button"
              onClick={goToDashboard}
              data-testid="signup-contact-skip"
              className="w-full flex justify-center py-2 px-4 text-sm font-medium text-gray-500 hover:text-gray-700"
            >
              Skip for now
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SignupContactDetails;
